import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  StarIcon, 
  TrashIcon, 
  PaperAirplaneIcon, 
  SparklesIcon,
  XMarkIcon,
  Cog6ToothIcon,
  VideoCameraIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  Bars3Icon,
  PencilIcon,
  PhotoIcon,
  DocumentIcon,
  XCircleIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';
import { Classroom, ScheduledMeeting } from '../types';
import { useClassrooms } from '../hooks/useClassrooms';
import { useClassroomCache } from '../hooks/useClassroomCache';
import { useAILearning } from '../hooks/useAILearning';
import { useUsers } from '../hooks/useUsers';
import { useAuth } from '../contexts/AuthContext';
import { useZoomMeetings } from '../hooks/useZoomMeetings';
import { useChat } from '../contexts/ChatContext';
import { CreateZoomMeeting } from '../components/CreateZoomMeeting';
import EditMeetingModal from '../components/EditMeetingModal';

import { ZoomMeetingsSection } from '../components/ZoomMeetingsSection';
import LoadingSpinner from '../components/LoadingSpinner';
import EditClassroomModal from '../components/EditClassroomModal';
import MemberManagementModal from '../components/MemberManagementModal';
import { renderTextWithLinks } from '../utils/formatUtils';
import { AIService } from '../utils/aiService';
import { db } from '../firebase/config';
import { doc, onSnapshot } from 'firebase/firestore';


const ClassroomDetail: React.FC = () => {
  const { classroomId } = useParams<{ classroomId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { sendMessage, isAdmin, deleteClassroom, deleteMessage } = useClassrooms();
  const { 
    getClassroom, 
    subscribeToClassroom, 
    unsubscribeFromClassroom,
    updateClassroomInCache,
    getCachedClassroom 
  } = useClassroomCache();
  const { users, fetchUsers, getUserDisplayName, getUserAvatar } = useUsers();
  const {
    meetings,
    createMeeting,
    updateMeeting,
    deleteMeeting,
    loading: meetingsLoading,
    joinMeeting,
    startMeeting,
    endMeeting,
    fetchRecordingLink,
    setError: setMeetingsError
  } = useZoomMeetings({ classroomId: classroomId || '' });

  const { openChat } = useChat();

  // State
  const [classroom, setClassroom] = useState<Classroom | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'discussion' | 'meetings'>('discussion');
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [showCreateMeeting, setShowCreateMeeting] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<ScheduledMeeting | null>(null);
  const [showLeftSidebarMobile, setShowLeftSidebarMobile] = useState(false);
  const [showRightSidebarMobile, setShowRightSidebarMobile] = useState(false);
  const [showMembersList, setShowMembersList] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<{ index: number; text: string } | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [aiSuggestForIndex, setAiSuggestForIndex] = useState<number | null>(null);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  const [aiSuggestError, setAiSuggestError] = useState<string | null>(null);
  const [onlineMembers, setOnlineMembers] = useState<Set<string>>(new Set());
  const [lastActivity, setLastActivity] = useState<Record<string, number>>({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Helper function to safely parse dates (handles both strings and Firestore Timestamps)
  const safeParseDate = useCallback((dateInput: any, includeTime: boolean = false) => {
    try {
      console.log('[ClassroomDetail] Date input type:', typeof dateInput, 'value:', dateInput);
      if (dateInput && typeof dateInput === 'object') {
        console.log('[ClassroomDetail] Date object keys:', Object.keys(dateInput));
        console.log('[ClassroomDetail] Date object methods:', Object.getOwnPropertyNames(dateInput));
      }
      
      let date: Date;
      
      // Handle Firestore Timestamp objects
      if (dateInput && typeof dateInput === 'object' && dateInput.toDate) {
        console.log('[ClassroomDetail] Using toDate() method');
        date = dateInput.toDate();
      } else if (dateInput && typeof dateInput === 'object' && dateInput.seconds) {
        // Handle Firestore Timestamp with seconds
        console.log('[ClassroomDetail] Using seconds conversion');
        date = new Date(dateInput.seconds * 1000);
      } else if (typeof dateInput === 'string') {
        // Handle string dates
        console.log('[ClassroomDetail] Using string conversion');
        date = new Date(dateInput);
      } else {
        console.warn('[ClassroomDetail] Unknown date format:', dateInput);
        return 'Invalid date';
      }
      
      if (isNaN(date.getTime())) {
        console.warn('[ClassroomDetail] Invalid date:', dateInput);
        return 'Invalid date';
      }
      
      if (includeTime) {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      }
      return date.toLocaleDateString();
    } catch (error) {
      console.error('[ClassroomDetail] Date parsing error:', error);
      return 'Date error';
    }
  }, []);

  // Check if user can manage classroom
  const canManageClassroom = currentUser && classroom && (
    classroom.createdBy === currentUser.uid || 
    classroom.admins?.includes(currentUser.uid) || 
    ['admin', 'author'].includes((currentUser as any).role)
  );

  // Function to scroll to bottom of messages with smooth animation
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      
      // Smooth scroll to bottom
      container.scrollTo({
        top: maxScrollTop,
        behavior: 'smooth'
      });
    }
  }, []);

  // Function to scroll to bottom instantly (for immediate updates)
  const scrollToBottomInstant = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      
      container.scrollTop = maxScrollTop;
    }
  }, []);

  // File upload functions
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      // Filter files by type and size
      const validFiles = files.filter(file => {
        const maxSize = 10 * 1024 * 1024; // 10MB limit
        const allowedTypes = [
          'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
          'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/plain', 'text/csv'
        ];
        
        if (file.size > maxSize) {
          alert(`File ${file.name} is too large. Maximum size is 10MB.`);
          return false;
        }
        
        if (!allowedTypes.includes(file.type)) {
          alert(`File type ${file.type} is not supported.`);
          return false;
        }
        
        return true;
      });
      
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  }, []);

  const removeFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearFiles = useCallback(() => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const getFileIcon = useCallback((file: File) => {
    if (file.type.startsWith('image/')) {
      return <PhotoIcon className="h-5 w-5 text-blue-500" />;
    } else if (file.type === 'application/pdf') {
      return <DocumentIcon className="h-5 w-5 text-red-500" />;
    } else if (file.type.includes('word') || file.type.includes('excel') || file.type.includes('powerpoint')) {
      return <DocumentIcon className="h-5 w-5 text-green-500" />;
    } else {
      return <DocumentIcon className="h-5 w-5 text-gray-500" />;
    }
  }, []);

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  // Handle sending messages
  const handleSendMessage = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    if (!classroom || !currentUser) return;

    try {
      setSending(true);
      console.log('Sending message:', messageText.trim());
      
      // Send message with attachments
      await sendMessage(classroom.id!, {
        senderId: currentUser.uid,
        senderName: currentUser.name || currentUser.email?.split('@')[0] || 'Unknown User',
        text: messageText.trim(),
        attachments: selectedFiles.length > 0 ? selectedFiles : undefined
      });

      console.log('Message sent successfully, clearing form');
      
      // Clear form and files
      setMessageText('');
      setSelectedFiles([]);
      clearFiles();
      
      // Reset upload progress
      setUploadProgress({});
      
      // Scroll to bottom instantly after sending message (like WhatsApp)
      scrollToBottomInstant();
      
      // Update user's online status
      const now = Date.now();
      setLastActivity(prev => ({
        ...prev,
        [currentUser.uid]: now
      }));
      setOnlineMembers(prev => new Set([...Array.from(prev), currentUser.uid]));
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Error sending message: ' + error);
    } finally {
      setSending(false);
    }
  }, [messageText, classroom, currentUser, sendMessage, scrollToBottomInstant, selectedFiles, clearFiles]);

  // Handle AI reply suggestions
  const handleAISuggestReply = useCallback(async (messageText: string, index: number) => {
    try {
      setAiSuggestForIndex(index);
      setAiSuggestLoading(true);
      setAiSuggestError(null);
      
      const ai = AIService.getInstance();
      const context = buildAIContextFromMessages(index);
      const prompt = `Draft a polite, helpful, and concise reply to the following message. Keep it clear and student-friendly.\n\nMessage:\n"${messageText}"`;
      
      const response = await ai.processChatMessage(currentUser?.uid || '', prompt, { context });
      setMessageText(response.response || '');
    } catch (error) {
      setAiSuggestError('Failed to generate AI reply');
      console.error('AI reply error:', error);
    } finally {
      setAiSuggestLoading(false);
    }
  }, [currentUser?.uid]);

  // Build AI context from recent messages
  const buildAIContextFromMessages = useCallback((currentIndex: number) => {
    if (!classroom || !classroom.messages) return '';
    
    const recentMessages = classroom.messages
      .slice(Math.max(0, currentIndex - 3), currentIndex)
      .map(msg => `${msg.senderName}: ${msg.text}`)
      .join('\n');
    
    return `Recent conversation context:\n${recentMessages}`;
  }, [classroom]);

  // Handle meeting operations
  const handleScheduleMeeting = useCallback(async (meetingData: any) => {
    try {
      const newMeeting = await createMeeting(meetingData);
        console.log('Zoom meeting created successfully:', newMeeting);
      setShowCreateMeeting(false);
    } catch (error) {
      console.error('Failed to create meeting:', error);
    }
  }, [createMeeting]);

  const handleJoinMeeting = useCallback(async (meeting: ScheduledMeeting) => {
    try {
      const joinUrl = await joinMeeting(meeting);
      if (joinUrl) {
        window.open(joinUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to join meeting:', error);
    }
  }, [joinMeeting]);

  const handleStartMeeting = useCallback(async (meeting: ScheduledMeeting) => {
    try {
      const startUrl = await startMeeting(meeting);
      if (startUrl) {
        window.open(startUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to start meeting:', error);
    }
  }, [startMeeting]);

  const handleEndMeeting = useCallback(async (meeting: ScheduledMeeting) => {
    try {
      await endMeeting(meeting);
    } catch (error) {
      console.error('Failed to end meeting:', error);
    }
  }, [endMeeting]);

  const handleEditMeeting = useCallback((meeting: ScheduledMeeting) => {
    setEditingMeeting(meeting);
  }, []);

  const handleDeleteMeeting = useCallback(async (meeting: ScheduledMeeting) => {
    try {
        await deleteMeeting(meeting.id);
      setEditingMeeting(null);
    } catch (error) {
      console.error('Failed to delete meeting:', error);
    }
  }, [deleteMeeting]);







  // Format timestamp
  const formatTimestamp = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  }, []);

  // Handle delete message
  const handleDeleteMessage = useCallback(async (messageIndex: number) => {
    if (!classroom || !currentUser) return;
    
    try {
      console.log('[ClassroomDetail] handleDeleteMessage called with index:', messageIndex);
      console.log('[ClassroomDetail] Current classroom:', classroom.id);
      console.log('[ClassroomDetail] Current user:', currentUser.uid);
      console.log('[ClassroomDetail] Current messages count:', classroom.messages?.length || 0);
      console.log('[ClassroomDetail] Message to delete:', classroom.messages?.[messageIndex]);
      
      await deleteMessage(classroom.id!, messageIndex, classroom);
      
      console.log('[ClassroomDetail] deleteMessage completed successfully');
    } catch (error) {
      console.error('[ClassroomDetail] Failed to delete message:', error);
      alert('Failed to delete message: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }, [classroom, currentUser, deleteMessage]);

  // Handle delete classroom
  const handleDeleteClassroom = useCallback(async () => {
    if (!classroom) return;
    
    try {
      await deleteClassroom(classroom.id!);
      navigate('/app/classrooms');
    } catch (error) {
      console.error('Failed to delete classroom:', error);
      setError('Failed to delete classroom. Please try again.');
      setShowDeleteConfirm(false);
    }
  }, [classroom, deleteClassroom, navigate]);

  // Check if user has scrolled up
  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      
      // Show scroll to bottom button if user has scrolled up more than 100px
      const hasScrolledUp = maxScrollTop - scrollTop > 100;
      setShowScrollToBottom(hasScrolledUp);
    }
  }, []);

  // Reset new message count when scrolling to bottom
  const handleScrollToBottom = useCallback(() => {
    scrollToBottomInstant();
    setNewMessageCount(0);
  }, [scrollToBottomInstant]);

  // Fetch classroom data with caching
  useEffect(() => {
    if (!classroomId) return;

    const fetchClassroom = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to get from cache first
        const cachedClassroom = getCachedClassroom(classroomId);
        if (cachedClassroom?.data) {
          console.log('[ClassroomDetail] Using cached classroom data:', cachedClassroom.data);
          setClassroom(cachedClassroom.data);
          setLoading(false);
        }

        // Fetch fresh data and subscribe to real-time updates
        const classroomData = await getClassroom(classroomId);
        if (classroomData) {
          console.log('[ClassroomDetail] Fetched fresh classroom data:', classroomData);
          setClassroom(classroomData);
                  // Note: We're using a direct Firestore listener instead of the cache hook subscription
        console.log('[ClassroomDetail] Using direct Firestore listener for real-time updates');
        } else {
          setError('Classroom not found');
          navigate('/app/classrooms');
        }
      } catch (err) {
        setError('Failed to load classroom');
        console.error('Error fetching classroom:', err);
    } finally {
        setLoading(false);
      }
    };

    fetchClassroom();

    // Note: Cleanup is handled by the direct Firestore listener
  }, [classroomId]);

  // Direct Firestore listener for real-time updates
  useEffect(() => {
    if (!classroomId) return;

    console.log('[ClassroomDetail] Setting up direct Firestore listener for classroom:', classroomId);
    
    const unsubscribe = onSnapshot(
      doc(db, 'classrooms', classroomId),
      (doc) => {
        if (doc.exists()) {
          const classroomData = { id: doc.id, ...doc.data() } as Classroom;
          console.log('[ClassroomDetail] Direct Firestore update received:', {
            id: classroomData.id,
            name: classroomData.name,
            messagesCount: classroomData.messages?.length || 0,
            lastMessage: classroomData.messages && classroomData.messages.length > 0 ? classroomData.messages[classroomData.messages.length - 1] : null,
            timestamp: new Date().toISOString(),
            operation: 'update'
          });
          setClassroom(classroomData);
        } else {
          console.log('[ClassroomDetail] Classroom document does not exist');
          setError('Classroom not found');
          navigate('/app/classrooms');
        }
      },
      (error) => {
        console.error('[ClassroomDetail] Error in direct Firestore listener:', error);
        setError('Failed to load classroom updates');
      }
    );

    return () => {
      console.log('[ClassroomDetail] Cleaning up direct Firestore listener');
      unsubscribe();
    };
  }, [classroomId, navigate]);

  // Fetch users when classroom changes
  useEffect(() => {
    if (classroom) {
      const userIdsToFetch = new Set<string>();
      
      // Add classroom members
      if (classroom.members && classroom.members.length > 0) {
        classroom.members.forEach(member => {
          // Handle both string IDs and member objects
          const memberId = typeof member === 'string' ? member : (member as any).userId;
          if (memberId) {
            userIdsToFetch.add(memberId);
          }
        });
      }
      
      // Add classroom creator
      if (classroom.createdBy) {
        userIdsToFetch.add(classroom.createdBy);
      }
      
      // Add classroom admins
      if (classroom.admins && classroom.admins.length > 0) {
        classroom.admins.forEach(adminId => userIdsToFetch.add(adminId));
      }
      
      // Add all message senders
      if (classroom.messages && classroom.messages.length > 0) {
        classroom.messages.forEach(message => {
          if (message.senderId) {
            userIdsToFetch.add(message.senderId);
          }
        });
      }
      
      // Debug logging
      console.log('[ClassroomDetail] Fetching users for classroom:', {
        classroomId: classroom.id,
        members: classroom.members?.length || 0,
        createdBy: classroom.createdBy,
        admins: classroom.admins?.length || 0,
        messageSenders: classroom.messages?.map(m => m.senderId) || [],
        totalUserIdsToFetch: userIdsToFetch.size,
        userIdsToFetch: Array.from(userIdsToFetch)
      });
      
      // Fetch all unique user IDs
      if (userIdsToFetch.size > 0) {
        fetchUsers(Array.from(userIdsToFetch));
      }
    }
  }, [classroom, fetchUsers]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (classroom?.messages && classroom.messages.length > 0) {
      scrollToBottom();
    }
  }, [classroom?.messages?.length]);

  // Scroll to bottom when component mounts
  useEffect(() => {
    scrollToBottom();
  }, []);

  // Auto-scroll to bottom when new messages arrive (like WhatsApp)
  useEffect(() => {
    if (classroom?.messages && classroom.messages.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [classroom?.messages?.length]);

  // Track new messages when user is scrolled up
  useEffect(() => {
    if (classroom?.messages) {
      const currentCount = classroom.messages.length || 0;
      if (currentCount > lastMessageCount && showScrollToBottom) {
        setNewMessageCount(prev => prev + (currentCount - lastMessageCount));
      }
      setLastMessageCount(currentCount);
    }
  }, [classroom?.messages?.length, lastMessageCount, showScrollToBottom]);

  // Debug: Log when classroom data changes
  useEffect(() => {
    if (classroom) {
      console.log('[ClassroomDetail] Classroom data updated:', {
        id: classroom.id,
        name: classroom.name,
        messagesCount: classroom.messages?.length || 0,
        lastMessage: classroom.messages && classroom.messages.length > 0 ? classroom.messages[classroom.messages.length - 1] : null,
        timestamp: new Date().toISOString()
      });
    }
  }, [classroom]);

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#1e1f22] z-[9999] flex items-center justify-center">
        <div className="text-center">
        <LoadingSpinner />
          <p className="text-gray-400 mt-4 text-lg">Loading classroom...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-[#1e1f22] z-[9999] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/app/classrooms')}
            className="px-4 py-2 bg-[#5865f2] text-white rounded-lg hover:bg-[#4854d4] transition-colors"
          >
            Back to Classrooms
          </button>
        </div>
      </div>
    );
  }

  // No classroom found
  if (!classroom) {
  return (
      <div className="fixed inset-0 bg-[#1e1f22] z-[9999] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-4">Classroom not found</p>
          <button
            onClick={() => navigate('/app/classrooms')}
            className="px-4 py-2 bg-[#5865f2] text-white rounded-lg hover:bg-[#4854d4] transition-colors"
          >
            Back to Classrooms
          </button>
              </div>
                </div>
    );
  }

  // Check if user is creator
  const isCreatorUser = classroom && currentUser && classroom.createdBy === currentUser.uid;
  const hasAdmins = classroom?.admins && Array.isArray(classroom.admins) && classroom.admins.length > 0;

  return (
    <div className="fixed inset-0 bg-[#1e1f22] z-[100]" style={{ isolation: 'isolate' }}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-[#2b2d31] border-b border-[#1e1f22] px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/app/classrooms')}
                className="p-2 rounded-lg bg-[#5865f2] text-white hover:bg-[#4854d4] transition-colors cursor-pointer"
                title="Go back to Classrooms"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-100">{classroom.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-gray-400 text-sm hidden sm:block">{classroom.description}</p>
                  {/* Online Members Indicator */}
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-xs text-green-400 font-medium">
                        {onlineMembers.size} online
                    </span>
                    </div>
                    <span className="text-gray-500 text-xs">•</span>
                    <span className="text-xs text-gray-400">
                      {classroom.members?.length || 0} total members
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                classroom.status === 'active' ? 'bg-green-900/50 text-green-300 border border-green-700/50' : 'bg-gray-900/50 text-gray-300 border border-gray-700/50'
                  }`}>
                    {classroom.status}
                  </span>
              {/* Mobile Navigation Buttons */}
              <div className="flex items-center gap-2 sm:hidden">
                <button
                  onClick={() => setShowLeftSidebarMobile(true)}
                  className="p-2 rounded bg-[#1e1f22] text-gray-200 hover:bg-[#313338] transition-colors"
                  title="Navigation"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setShowRightSidebarMobile(true)}
                  className="p-2 rounded bg-[#1e1f22] text-gray-200 hover:bg-[#313338] transition-colors"
                  title="Info"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                </div>
              </div>
                            </div>
                            </div>

        {/* Main Layout */}
        <div className="flex-1 flex min-h-0">
          {/* Left Sidebar - Navigation */}
          <div className="hidden lg:flex w-64 bg-[#1e1f22] border-r border-[#2b2d31] flex-col">
            {/* Quick Actions */}
            <div className="p-4 border-b border-[#2b2d31]">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Quick Actions</h3>
              <div className="space-y-2">
                {isCreatorUser && (
                  <>
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="w-full flex items-center px-3 py-2 text-sm text-gray-200 hover:bg-[#2b2d31] rounded-lg transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit Classroom
                    </button>
                    <button
                      onClick={() => setShowMemberModal(true)}
                      className="w-full flex items-center px-3 py-2 text-sm text-gray-200 hover:bg-[#2b2d31] rounded-lg transition-colors"
                    >
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      Manage Members
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full flex items-center px-3 py-2 text-sm text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Delete Classroom
                    </button>
                  </>
                )}
                  <button
                  onClick={() => openChat({ fullScreen: true })}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-200 hover:bg-[#2b2d31] rounded-lg transition-colors"
                  >
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  Profstudymate
                  </button>
          </div>
        </div>

            {/* Navigation Tabs */}
            <div className="flex-1 p-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Navigation</h3>
              <div className="space-y-1">
                <button
                  onClick={() => setActiveTab('discussion')}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeTab === 'discussion'
                      ? 'bg-[#5865f2] text-white'
                      : 'text-gray-300 hover:bg-[#2b2d31] hover:text-gray-200'
                  }`}
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  <span>Discussion</span>
                    {(classroom.messages && classroom.messages.length > 0) && (
                    <span className="ml-auto px-2 py-1 text-xs font-medium bg-white/20 text-white rounded-full">
                        {classroom.messages.length}
                      </span>
                    )}
                </button>
                <button
                  onClick={() => setActiveTab('meetings')}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeTab === 'meetings'
                      ? 'bg-[#5865f2] text-white'
                      : 'text-gray-300 hover:bg-[#2b2d31] hover:text-gray-200'
                  }`}
                >
                  <VideoCameraIcon className="h-4 w-4 mr-2" />
                    <span>Meetings</span>
                    {(() => {
                      const ongoingCount = meetings.filter(meeting => {
                        const now = new Date();
                        const startTime = new Date(meeting.startTime);
                        const endTime = new Date(startTime.getTime() + (meeting.duration || 60) * 60000);
                        return meeting.status !== 'cancelled' && meeting.status !== 'finished' && now >= startTime && now <= endTime;
                      }).length;
                      
                      const upcomingCount = meetings.filter(meeting => {
                        const now = new Date();
                        const startTime = new Date(meeting.startTime);
                        return meeting.status !== 'cancelled' && meeting.status !== 'finished' && startTime > now;
                      }).length;
                      
                      if (ongoingCount > 0) {
                        return (
                        <span className="ml-auto px-2 py-1 text-xs font-medium bg-red-900/50 text-red-300 border border-red-700/50 rounded-full">
                            Live
                          </span>
                        );
                      } else if (upcomingCount > 0) {
                        return (
                        <span className="ml-auto px-2 py-1 text-xs font-medium bg-blue-900/50 text-blue-300 border border-blue-700/50 rounded-full">
                            Scheduled
                          </span>
                        );
                      }
                      return null;
                    })()}
                </button>
                  </div>
            </div>

            {/* Classroom Info */}
            <div className="p-4 border-t border-[#2b2d31]">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Classroom Info</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Category:</span>
                  <span className="font-medium text-gray-200">{classroom.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Subject:</span>
                  <span className="font-medium text-gray-200">{classroom.subject}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Members:</span>
                  <span className="font-medium text-gray-200">{(classroom.members?.length || 0)}/{classroom.maxMembers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Created:</span>
                  <span className="font-medium text-gray-200">
                    {safeParseDate(classroom.createdAt, true)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Created by:</span>
                  <span className="font-medium text-gray-200">{getUserDisplayName(classroom.createdBy)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Center Content Area */}
          <div className="flex-1 bg-[#313338] flex flex-col">
            {/* Content Header */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-[#1e1f22]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold text-gray-100">
                    {activeTab === 'discussion' ? 'Discussion' : 'Meetings'}
                  </h2>
                  {activeTab === 'discussion' && (classroom.messages && classroom.messages.length > 0) && (
                    <span className="px-2 py-1 text-xs font-medium bg-[#1e1f22] text-gray-300 rounded-full">
                      {classroom.messages.length} messages
                    </span>
                  )}
                </div>
                {activeTab === 'meetings' && currentUser && classroom.createdBy === currentUser.uid && (
                  <button
                    onClick={() => setShowCreateMeeting(true)}
                    className="px-3 py-1.5 text-xs bg-[#5865f2] hover:bg-[#4854d4] text-white rounded"
                  >
                    New Meeting
                </button>
                )}
              </div>
              </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {activeTab === 'discussion' ? (
                /* Discussion Content */
                  <div className="space-y-4">
                      {(classroom.messages && classroom.messages.length === 0) ? (
                    <div className="text-center text-gray-400 py-12">
                      <div className="w-16 h-16 mx-auto mb-4 bg-[#1e1f22] rounded-full flex items-center justify-center">
                        <PaperAirplaneIcon className="h-8 h-8 text-gray-500" />
                          </div>
                      <p className="text-lg font-medium text-gray-200 mb-2">No messages yet</p>
                      <p className="text-gray-400">Start the conversation by sending the first message!</p>
                        </div>
                      ) : (
                    <div 
                      className="space-y-4" 
                      ref={messagesContainerRef}
                      onScroll={handleScroll}
                    >
                      {(classroom.messages || []).map((message, index) => {
                          const displayName = getUserDisplayName(message.senderId);
                          const avatar = getUserAvatar(message.senderId);
                          const isCreator = message.senderId === classroom.createdBy;
                          
                          // Use senderName from message as fallback if user data not available
                          const finalDisplayName = displayName === 'Unknown User' && message.senderName 
                            ? message.senderName 
                            : displayName;
                          
                          // Debug logging for message rendering
                          console.log('[ClassroomDetail] Rendering message:', {
                            messageIndex: index,
                            senderId: message.senderId,
                            senderName: message.senderName,
                            displayName,
                            finalDisplayName,
                            avatar,
                            userInState: !!users[message.senderId],
                            userData: users[message.senderId]
                          });
                          
                          return (
                            <div key={index} className="flex space-x-3 group">
                              <div className="flex-shrink-0 relative">
                                {avatar ? (
                                  <img
                                    src={avatar}
                                    alt={finalDisplayName}
                                    className="w-10 h-10 rounded-full object-cover shadow-sm"
                                  />
                                ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-[#5865f2] to-[#4854d4] rounded-full flex items-center justify-center shadow-sm">
                                    <span className="text-white text-sm font-medium">
                                      {finalDisplayName.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Star for creator */}
                                {isCreator && (
                                  <div className="absolute -top-1 -right-1">
                                  <StarIcon className="h-4 w-4 text-yellow-400 drop-shadow-sm" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-semibold text-gray-200 flex items-center">
                                    {finalDisplayName}
                                    {isCreator && (
                                    <StarIcon className="h-3 w-3 ml-1 text-yellow-400" />
                                    )}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatTimestamp(message.timestamp)}
                                  </span>
                                </div>
                              <div className="text-sm text-gray-300 bg-[#1e1f22] rounded-xl px-4 py-3 shadow-sm group-hover:shadow-md transition-shadow border border-[#313338]">
                                  <div dangerouslySetInnerHTML={{ __html: renderTextWithLinks(message.text) }} />
                                  
                                  {/* Attachments Display */}
                                  {message.attachments && message.attachments.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-[#313338]">
                                      <div className="space-y-2">
                                        {message.attachments.map((attachment, attachmentIndex) => (
                                          <div key={attachmentIndex} className="flex items-center space-x-3 p-2 bg-[#313338] rounded-lg border border-[#1e1f22]">
                                            {attachment.type.startsWith('image/') ? (
                                              <img
                                                src={attachment.url}
                                                alt={attachment.name}
                                                className="w-12 h-12 object-cover rounded border border-[#1e1f22]"
                                                loading="lazy"
                                              />
                                            ) : (
                                              <div className="w-12 h-12 bg-[#1e1f22] rounded border border-[#313338] flex items-center justify-center">
                                                {getFileIcon({ type: attachment.type, name: attachment.name, size: attachment.size } as File)}
                                              </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm text-gray-200 truncate">{attachment.name}</p>
                                              <p className="text-xs text-gray-400">{formatFileSize(attachment.size)}</p>
                                            </div>
                                            <a
                                              href={attachment.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex-shrink-0 p-2 text-gray-400 hover:text-[#5865f2] hover:bg-[#1e1f22] rounded transition-colors"
                                              title="Download file"
                                            >
                                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                              </svg>
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  <div className="mt-2 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => handleAISuggestReply(message.text, index)}
                                      className="inline-flex items-center px-2 py-1 text-xs bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded hover:from-purple-700 hover:to-purple-800"
                                      title="Ask AI to suggest a reply"
                                    >
                                      <SparklesIcon className="h-3 w-3 mr-1" /> AI Reply
                                    </button>
                                    {message.senderId === currentUser?.uid && (
                                      <button
                                        onClick={() => setMessageToDelete({ index, text: message.text })}
                                        className="inline-flex items-center px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                        title="Delete message"
                                      >
                                        <TrashIcon className="h-3 w-3 mr-1" /> Delete
                                      </button>
                                    )}
                                  </div>
                                  {aiSuggestForIndex === index && (
                                  <div className="mt-2 border-t border-[#313338] pt-2">
                                      {aiSuggestError && (
                                      <div className="text-xs text-red-400 mb-1">{aiSuggestError}</div>
                                      )}
                                      {aiSuggestLoading ? (
                                      <div className="text-xs text-gray-400 flex items-center">
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500 mr-2"></div>
                                          Generating reply...
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                        <div className="text-xs text-gray-200 bg-[#313338] border border-[#1e1f22] rounded p-2 whitespace-pre-wrap">
                                          {messageText || 'No suggestion generated.'}
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <button
                                              type="button"
                                            onClick={() => setMessageText(messageText)}
                                            disabled={!messageText}
                                            className="px-2 py-1 text-xs bg-[#5865f2] text-white rounded disabled:opacity-50"
                                            >
                                              Use as Reply
                                            </button>
                                            <button
                                              type="button"
                                            onClick={() => { setAiSuggestForIndex(null); setMessageText(''); setAiSuggestError(''); }}
                                            className="px-2 py-1 text-xs bg-[#1e1f22] text-gray-300 rounded"
                                            >
                                              Close
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                      })}
                    </div>
                      )}
                  </div>
                ) : (
                /* Meetings Content */
                  <div className="space-y-4">
                    <ZoomMeetingsSection
                      meetings={meetings}
                      onCreateMeeting={() => setShowCreateMeeting(true)}
                      onJoinMeeting={handleJoinMeeting}
                      onStartMeeting={handleStartMeeting}
                      onEndMeeting={handleEndMeeting}
                      onEditMeeting={handleEditMeeting}
                      onDeleteMeeting={handleDeleteMeeting}
                      isAdmin={canManageClassroom || false}
                    isCreator={Boolean(currentUser && classroom.createdBy === currentUser.uid)}
                      currentUserId={currentUser?.uid}
                      loading={meetingsLoading}
                    />
                  </div>
                )}
              </div>
            </div>

          {/* Right Sidebar - Info Panel */}
          <div className="hidden lg:flex w-80 bg-[#2b2d31] border-l border-[#1e1f22] flex-col">
            {/* Members Section */}
            <div className="p-4 border-b border-[#1e1f22]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Members ({classroom.members?.length || 0})</h3>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-400 font-medium">{onlineMembers.size} online</span>
          </div>
              </div>
              <div className="space-y-3">
                {(classroom.members || []).slice(0, 8).map((member) => {
                  // Handle both string IDs and member objects
                  const memberId = typeof member === 'string' ? member : (member as any).userId;
                  const displayName = getUserDisplayName(memberId);
                  const avatar = getUserAvatar(memberId);
                  const isCreator = memberId === classroom.createdBy;
                  const isMemberAdmin = classroom.admins?.includes(memberId) || false;
                  const isOnline = onlineMembers.has(memberId);
                  return (
                    <div key={memberId} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-[#313338] transition-colors">
                      <div className="relative">
                      {avatar ? (
                        <img src={avatar} alt={displayName} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5865f2] to-[#4854d4] flex items-center justify-center text-white font-medium text-sm">
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                      )}
                        {/* Online indicator */}
                        {isOnline && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 border-2 border-[#2b2d31] rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-200 truncate">{displayName}</p>
                        <div className="flex items-center gap-1 mt-1">
                        {isCreator && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-full bg-yellow-900/50 text-yellow-300 border border-yellow-700/50">
                              <StarIcon className="h-2 w-2 mr-1" />
                              Creator
                            </span>
                          )}
                          {isMemberAdmin && !isCreator && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-full bg-blue-900/50 text-blue-300 border border-blue-700/50">
                              <Cog6ToothIcon className="h-2 w-2 mr-1" />
                              Admin
                            </span>
                          )}
                          {isOnline && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-full bg-green-900/50 text-green-300 border border-green-700/50">
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                              Online
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(classroom.members && classroom.members.length > 8) && (
                  <button
                    onClick={() => setShowMembersList(true)}
                    className="w-full text-center text-sm text-[#5865f2] hover:text-[#4854d4] py-2 hover:bg-[#313338] rounded-lg transition-colors"
                  >
                    View all {(classroom.members?.length || 0)} members
                  </button>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="p-4 border-b border-[#1e1f22]">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Recent Activity</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center justify-between">
                  <span>Last message:</span>
                  <span className="text-gray-300">
                    {(classroom.messages && classroom.messages.length > 0) 
                      ? formatTimestamp(classroom.messages[classroom.messages.length - 1].timestamp)
                      : 'None'
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Last meeting:</span>
                  <span className="text-gray-300">
                    {(meetings && meetings.length > 0) 
                      ? safeParseDate(meetings[0].startTime, true)
                      : 'None'
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Quick Stats</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-[#1e1f22] p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-[#5865f2]">{classroom.messages ? classroom.messages.length : 0}</div>
                  <div className="text-xs text-gray-400 uppercase">Messages</div>
                </div>
                <div className="bg-[#1e1f22] p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-[#5865f2]">{meetings ? meetings.length : 0}</div>
                  <div className="text-xs text-gray-400 uppercase">Meetings</div>
                </div>
                <div className="bg-[#1e1f22] p-3 rounded-lg text-center col-span-2">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <div className="text-lg font-bold text-green-400">{onlineMembers.size}</div>
                    <div className="text-xs text-gray-400 uppercase">Online Now</div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Input Bar */}
        <div className="flex-shrink-0 bg-[#2b2d31] border-t border-[#1e1f22] p-3">
          <div className="lg:ml-80 lg:mr-80">
            {/* File Upload Area */}
            {selectedFiles.length > 0 && (
              <div className="mb-3 p-3 bg-[#1e1f22] rounded-lg border border-[#313338]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-300">Selected Files ({selectedFiles.length})</span>
                  <div className="flex items-center gap-2">
                    {sending && (
                      <div className="flex items-center gap-2 text-xs text-blue-400">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-400"></div>
                        <span>Uploading...</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={clearFiles}
                      disabled={sending}
                      className="text-xs text-gray-400 hover:text-gray-300 hover:bg-[#2b2d31] px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-[#313338] rounded border border-[#1e1f22]">
                      <div className="flex items-center space-x-2">
                        {getFileIcon(file)}
                        <div className="min-w-0">
                          <p className="text-sm text-gray-200 truncate">{file.name}</p>
                          <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-400 hover:bg-[#1e1f22] p-1 rounded transition-colors"
                        title="Remove file"
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}



            <form onSubmit={handleSendMessage} className="flex items-center gap-3">
              {/* File Upload Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                className="flex-shrink-0 p-3 text-gray-400 hover:text-gray-200 hover:bg-[#1e1f22] rounded-xl transition-colors border border-[#1e1f22] hover:border-[#313338] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Attach files"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
                ) : (
                  <PaperClipIcon className="h-5 w-5" />
                )}
              </button>
              
              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              />

              <input
                type="text"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 min-w-0 px-4 py-3 border border-[#1e1f22] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5865f2] focus:border-[#5865f2] bg-[#1e1f22] text-gray-200 placeholder-gray-400 shadow-sm"
              />
           
              <button
                type="submit"
                disabled={!messageText.trim()}
                className="flex-shrink-0 px-6 py-3 bg-[#5865f2] text-white rounded-xl hover:bg-[#4854d4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5865f2] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Send</span>
              </button>
            </form>
          </div>
        </div>
      </div>

            {/* Modals */}
      {showEditModal && classroom && (
        <EditClassroomModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          classroom={classroom}
        />
      )}

      {showMemberModal && classroom && (
        <MemberManagementModal
          isOpen={showMemberModal}
          onClose={() => setShowMemberModal(false)}
          classroom={classroom}
        />
      )}

              {/* Create Zoom Meeting Modal */}
        {showCreateMeeting && (
          <CreateZoomMeeting
            isOpen={showCreateMeeting}
            onCancel={() => setShowCreateMeeting(false)}
            onCreateMeeting={handleScheduleMeeting}
            classroom={classroom}
          />
        )}
        
        {/* Debug display */}
        {/* Removed debug display */}

      {editingMeeting && (
        <EditMeetingModal
          isOpen={!!editingMeeting}
          onClose={() => setEditingMeeting(null)}
          meeting={editingMeeting}
          onSave={async (updates) => {
            try {
              await updateMeeting(editingMeeting.id, {
                topic: updates.topic,
                startTime: updates.startTime,
                duration: updates.duration,
              });
            } catch (e) {
          
            }
          }}
          onDelete={async (meetingId) => {
            try {
              await deleteMeeting(meetingId);
              setEditingMeeting(null);
            } catch (e) {
              
            }
          }}
        />
      )}



      {/* All Members Modal */}
      {showMembersList && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMembersList(false)}></div>
          <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Classroom Members ({classroom.members?.length || 0})</h3>
              <button onClick={() => setShowMembersList(false)} className="text-gray-500 hover:text-gray-700">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              <div className="space-y-3">
                {(classroom.members || []).map((member) => {
                  // Handle both string IDs and member objects
                  const memberId = typeof member === 'string' ? member : (member as any).userId;
                  const displayName = getUserDisplayName(memberId);
                  const avatar = getUserAvatar(memberId);
                  const isCreator = memberId === classroom.createdBy;
                  const isMemberAdmin = classroom.admins?.includes(memberId) || false;
                  return (
                    <div key={memberId} className="flex items-center p-2 border border-gray-200 rounded-lg">
                      {avatar ? (
                        <img src={avatar} alt={displayName} className="w-10 h-10 rounded-full object-cover mr-3" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-medium mr-3">
                          {displayName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{displayName}</p>
                        <p className="text-xs text-gray-500">
                          {isCreator ? 'Classroom Creator' : isMemberAdmin ? 'Administrator' : 'Member'}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[10001] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteConfirm(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                    </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Classroom
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete "{classroom?.name}"? This action cannot be undone and will remove all messages and member data.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDeleteClassroom}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Delete Confirmation Modal */}
      {messageToDelete && (
        <div className="fixed inset-0 z-[10001] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setMessageToDelete(null)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete Message
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this message? This action cannot be undone.
                      </p>
                      <div className="mt-2 p-2 bg-gray-50 rounded border text-xs text-gray-600">
                        "{messageToDelete.text.length > 100 ? messageToDelete.text.substring(0, 100) + '...' : messageToDelete.text}"
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => {
                      handleDeleteMessage(messageToDelete.index);
                      setMessageToDelete(null);
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setMessageToDelete(null)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

              {/* Scroll to Bottom Button */}
        {showScrollToBottom && (
          <button
            onClick={handleScrollToBottom}
            className="fixed bottom-20 right-4 p-3 bg-[#5865f2] text-white rounded-full shadow-lg hover:bg-[#4854d4] transition-all duration-200 z-50 group"
            title="Scroll to bottom"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V6" />
            </svg>
            {newMessageCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                {newMessageCount > 99 ? '99+' : newMessageCount}
              </span>
            )}
            <div className="absolute inset-0 bg-white/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          </button>
        )}

      {/* Mobile Overlays */}
      {/* Left Sidebar Mobile Overlay */}
      {showLeftSidebarMobile && (
        <div className="lg:hidden fixed inset-0 z-[70] bg-black/50" onClick={() => setShowLeftSidebarMobile(false)}>
          <div className="absolute left-0 top-0 h-full w-80 bg-[#1e1f22] border-r border-[#2b2d31]" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#2b2d31]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-200">Navigation</h3>
                <button
                  onClick={() => setShowLeftSidebarMobile(false)}
                  className="p-2 text-gray-400 hover:text-gray-300 rounded-lg hover:bg-[#2b2d31] transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="p-4 border-b border-[#2b2d31]">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Quick Actions</h3>
              <div className="space-y-2">
                {isCreatorUser && (
                  <>
                    <button
                      onClick={() => {
                        setShowEditModal(true);
                        setShowLeftSidebarMobile(false);
                      }}
                      className="w-full flex items-center px-3 py-2 text-sm text-gray-200 hover:bg-[#2b2d31] rounded-lg transition-colors"
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      Edit Classroom
                    </button>
                    <button
                      onClick={() => {
                        setShowMemberModal(true);
                        setShowLeftSidebarMobile(false);
                      }}
                      className="w-full flex items-center px-3 py-2 text-sm text-gray-200 hover:bg-[#2b2d31] rounded-lg transition-colors"
                    >
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      Manage Members
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(true);
                        setShowLeftSidebarMobile(false);
                      }}
                      className="w-full flex items-center px-3 py-2 text-sm text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Delete Classroom
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    openChat({ fullScreen: true });
                    setShowLeftSidebarMobile(false);
                  }}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-200 hover:bg-[#2b2d31] rounded-lg transition-colors"
                >
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  Profstudymate
                </button>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="p-4 border-b border-[#2b2d31]">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Navigation</h3>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setActiveTab('discussion');
                    setShowLeftSidebarMobile(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeTab === 'discussion'
                      ? 'bg-[#5865f2] text-white'
                      : 'text-gray-300 hover:bg-[#2b2d31] hover:text-gray-200'
                  }`}
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  <span>Discussion</span>
                  {(classroom.messages && classroom.messages.length > 0) && (
                    <span className="ml-auto px-2 py-1 text-xs font-medium bg-white/20 text-white rounded-full">
                      {classroom.messages.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => {
                    setActiveTab('meetings');
                    setShowLeftSidebarMobile(false);
                  }}
                  className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeTab === 'meetings'
                      ? 'bg-[#5865f2] text-white'
                      : 'text-gray-300 hover:bg-[#2b2d31] hover:text-gray-200'
                  }`}
                >
                  <VideoCameraIcon className="h-4 w-4 mr-2" />
                  <span>Meetings</span>
                  {(() => {
                    const ongoingCount = meetings.filter(meeting => {
                      const now = new Date();
                      const startTime = new Date(meeting.startTime);
                      const endTime = new Date(startTime.getTime() + (meeting.duration || 60) * 60000);
                      return meeting.status !== 'cancelled' && meeting.status !== 'finished' && now >= startTime && now <= endTime;
                    }).length;
                    
                    const upcomingCount = meetings.filter(meeting => {
                      const now = new Date();
                      const startTime = new Date(meeting.startTime);
                      return meeting.status !== 'cancelled' && meeting.status !== 'finished' && startTime > now;
                    }).length;
                    
                    if (ongoingCount > 0) {
                      return (
                        <span className="ml-auto px-2 py-1 text-xs font-medium bg-red-900/50 text-red-300 border border-red-700/50 rounded-full">
                          Live
                        </span>
                      );
                    } else if (upcomingCount > 0) {
                      return (
                        <span className="ml-auto px-2 py-1 text-xs font-medium bg-blue-900/50 text-blue-300 border border-blue-700/50 rounded-full">
                          Scheduled
                        </span>
                      );
                    }
                    return null;
                  })()}
                </button>
              </div>
            </div>

            {/* Classroom Info */}
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Classroom Info</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Category:</span>
                  <span className="font-medium text-gray-200">{classroom.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Subject:</span>
                  <span className="font-medium text-gray-200">{classroom.subject}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Members:</span>
                  <span className="font-medium text-gray-200">{(classroom.members?.length || 0)}/{classroom.maxMembers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Created:</span>
                  <span className="font-medium text-gray-200">{safeParseDate(classroom.createdAt, true)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Created by:</span>
                  <span className="font-medium text-gray-200">{getUserDisplayName(classroom.createdBy)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right Sidebar Mobile Overlay */}
      {showRightSidebarMobile && (
        <div className="lg:hidden fixed inset-0 z-[70] bg-black/50" onClick={() => setShowRightSidebarMobile(false)}>
          <div className="absolute right-0 top-0 h-full w-80 bg-[#2b2d31] border-l border-[#1e1f22]" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-[#1e1f22]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-200">Classroom Info</h3>
                <button
                  onClick={() => setShowRightSidebarMobile(false)}
                  className="p-2 text-gray-400 hover:text-gray-300 rounded-lg hover:bg-[#313338] transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Members Section */}
            <div className="p-4 border-b border-[#1e1f22] ">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Members ({(classroom.members?.length || 0)})</h3>
              <div className="space-y-3">
                {(classroom.members || []).slice(0, 8).map((member) => {
                  // Handle both string IDs and member objects
                  const memberId = typeof member === 'string' ? member : (member as any).userId;
                  const displayName = getUserDisplayName(memberId);
                  const avatar = getUserAvatar(memberId);
                  const isCreator = memberId === classroom.createdBy;
                  const isMemberAdmin = classroom.admins?.includes(memberId) || false;
                  const isOnline = onlineMembers.has(memberId);
                  return (
                    <div key={memberId} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-[#313338] transition-colors">
                      <div className="relative">
                        {avatar ? (
                          <img src={avatar} alt={displayName} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5865f2] to-[#4854d4] flex items-center justify-center text-white font-medium text-sm">
                            {displayName.charAt(0).toUpperCase()}
                          </div>
                        )}
                        {/* Online indicator */}
                        {isOnline && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 border-2 border-[#2b2d31] rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-200 truncate">{displayName}</p>
                        <div className="flex items-center gap-1 mt-1">
                          {isCreator && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-full bg-yellow-900/50 text-yellow-300 border border-yellow-700/50">
                              <StarIcon className="h-2 w-2 mr-1" />
                              Creator
                            </span>
                          )}
                          {isMemberAdmin && !isCreator && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-full bg-blue-900/50 text-blue-300 border border-blue-700/50">
                              <Cog6ToothIcon className="h-2 w-2 mr-1" />
                              Admin
                            </span>
                          )}
                          {isOnline && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded-full bg-green-900/50 text-green-300 border border-green-700/50">
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                              Online
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(classroom.members && classroom.members.length > 8) && (
                  <button
                    onClick={() => {
                      setShowMembersList(true);
                      setShowRightSidebarMobile(false);
                    }}
                    className="w-full text-center text-sm text-[#5865f2] hover:text-[#4854d4] py-2 hover:bg-[#313338] rounded-lg transition-colors"
                  >
                    View all {(classroom.members?.length || 0)} members
                  </button>
                )}
              </div>

            {/* Recent Activity */}
            <div className="p-4 border-b border-[#1e1f22] ">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Recent Activity</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center justify-between">
                  <span>Last message:</span>
                  <span className="text-gray-300">
                    {(classroom.messages && classroom.messages.length > 0) 
                      ? formatTimestamp(classroom.messages[classroom.messages.length - 1].timestamp)
                      : 'None'
                    }
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Last meeting:</span>
                  <span className="text-gray-300">
                    {(meetings && meetings.length > 0) 
                      ? safeParseDate(meetings[0].startTime, true)
                      : 'None'
                    }
                  </span>
                </div>
              </div>
            </div>
            </div>


            {/* Quick Stats */}
            <div className="p-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Quick Stats</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-[#1e1f22] p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-[#5865f2]">{classroom.messages ? classroom.messages.length : 0}</div>
                  <div className="text-xs text-gray-400 uppercase">Messages</div>
                </div>
                <div className="bg-[#1e1f22] p-3 rounded-lg text-center">
                  <div className="text-2xl font-bold text-[#5865f2]">{meetings ? meetings.length : 0}</div>
                  <div className="text-xs text-gray-400 uppercase">Meetings</div>
                </div>
                <div className="bg-[#1e1f22] p-3 rounded-lg text-center col-span-2">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                    <div className="text-lg font-bold text-green-400">{onlineMembers.size}</div>
                    <div className="text-xs text-gray-400 uppercase">Online Now</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default ClassroomDetail;
